"use client";

import Logo from "@features/iTICKET-logo";
import { Flex, Text, Link as LinkRadix } from "@radix-ui/themes";
import { WhitelabelConfig } from "@types";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useSelectedLayoutSegments } from "next/navigation";

interface Props {
  menu: WhitelabelConfig["settings"]["footer"]["menu"];
  contact: WhitelabelConfig["contact"];
  organisationName: string;
  requiresCookieConsent?: boolean;
}

const WhitelabelFooter = ({
  menu,
  organisationName,
  contact,
  requiresCookieConsent,
}: Props) => {
  const segments = useSelectedLayoutSegments();
  const t = useTranslations("Footer");

  if (segments.includes("payment")) {
    return <div />;
  }

  const email = contact?.email || "support@iticket.io";

  return (
    <footer className="relative bottom-0 flex flex-col items-start justify-between w-full gap-y-2 px-10 py-8 bg-black md:p-14">
      <Flex
        display="flex"
        direction="column"
        gap="0.5rem"
        minWidth="max-content"
      >
        {menu.map((menuItem) => (
          <div key={menuItem[0]} className="w-fit">
            <a
              className="inline text-white peer"
              href={menuItem[1]}
              target="_blank"
              rel="noreferrer"
            >
              {menuItem[0]}
            </a>
            <hr className="h-[1px] opacity-50 w-10 peer-hover:w-full transition-all ease-in-out duration-200" />
          </div>
        ))}
        <div className="w-fit">
          <Link
            prefetch={false}
            className="inline text-white peer"
            href="/legal/terms"
          >
            {t("legalterms")}
          </Link>
          <hr className="h-[1px] opacity-50 w-10 peer-hover:w-full transition-all ease-in-out duration-200" />
        </div>
        <div className="w-fit">
          <Link
            prefetch={false}
            className="inline text-white peer"
            href="/legal/privacy"
          >
            {t("legalprivacy")}
          </Link>
          <hr className="h-[1px] opacity-50 w-10 peer-hover:w-full transition-all ease-in-out duration-200" />
        </div>
        {requiresCookieConsent && (
          <div className="w-fit">
            <Link
              prefetch={false}
              className="inline text-white peer"
              href="/cookie-declaration"
            >
              {t("cookiedeclaration")}
            </Link>
            <hr className="h-[1px] opacity-50 w-10 peer-hover:w-full transition-all ease-in-out duration-200" />
          </div>
        )}
        <Text className="text-white">
          {t("contact")}
          {" | "}
          <LinkRadix href={`mailto:${email}`}>{email}</LinkRadix>
        </Text>
      </Flex>
      <div className="flex flex-col sm:flex-row gap-y-2 self-start sm:items-center sm:justify-between w-full md:items-end">
        <span className="text-sm text-gray-200 md:text-base">
          {organisationName}
        </span>
        <Link
          href="https://hello.iticket.co.nz"
          target="_blank"
          rel="noreferrer"
          className="flex flex-row items-center text-sm text-gray-200 gap-x-2"
        >
          <Text>{t("poweredby")}</Text>
          <span className="sr-only">iTICKET</span>
          <Logo size="20" />
        </Link>
      </div>
    </footer>
  );
};

export default WhitelabelFooter;
